import React, { Suspense, useEffect, useRef } from "react";
import ThemeProvider from "./theme";
import { BrowserRouter, useLocation, matchPath } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "./components/ScrollToTop";
import Routes from "./routes/Routes";
import { styled, Box } from "@mui/material";
import { SnackbarProvider } from "notistack";
import Loader from "./components/Loader";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "./redux/store";
import { GoogleOAuthProvider } from "@react-oauth/google";
import creatorRoutes from "./routes/CreatorRoutes";
import brandRoutes from "./routes/BrandRoutes";
import { requestPermission } from "./firebase/firebase";
import "./style/App.css";
import { LanguageProvider } from "./context/LanguageContext";
import "./i18n/i18n";

// Alert notification styling
const StyledSnackbarProvider = styled(SnackbarProvider)`
  font-family: "General Sans", sans-serif !important;
  &.notistack-MuiContent-success {
    background-color: #ffcf00;
    color: #000000;
  }
  &.notistack-MuiContent-error {
    background-color: #cd4628;
    color: #ffffff;
  }
  &.notistack-MuiContent {
    box-shadow: 0px 3px 2px -2px rgba(0, 0, 0, 0.06),
      0px 5px 3px -2px rgba(0, 0, 0, 0.02);
    font-weight: 500;
    border-radius: 12px;
  }
`;

const Layout = () => {
  const location = useLocation();
  const isRegisterTypePage = location.pathname === "/register";
  const isRegisterPage = location.pathname.startsWith("/register/");
  const isCreatorOrBrandPage =
    location.pathname.startsWith("/creator") ||
    location.pathname.startsWith("/brand");

  // List all valid routes
  const validRoutes = [
    "/",
    "/register",
    "/register/brand",
    "/register/creator",
    ...creatorRoutes.map((route) => route.path),
    ...brandRoutes.map((route) => route.path),
  ];

  // Check if the current route exists in the valid routes
  const isValidRoute = validRoutes.some((route) =>
    matchPath(route, location.pathname)
  );

  return (
    <>
      {isValidRoute && !isRegisterPage && <Header />}
      <Suspense fallback={<Loader />}>
        <Box
          sx={
            isValidRoute &&
            !isRegisterPage &&
            !isRegisterTypePage &&
            !isCreatorOrBrandPage
              ? { pt: { xs: "64px", sm: "70px" } }
              : {}
          }
        >
          <Routes />
        </Box>
      </Suspense>
      {isValidRoute && !isRegisterPage && !isRegisterTypePage && <Footer />}
    </>
  );
};

const App = () => {
  const btnRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      const isAndroid = /Android/i.test(navigator.userAgent);

      if (isIOS || /apple/i.test(navigator.vendor)) {
        requestPermission();
      } else if (
        isAndroid ||
        (!isIOS && localStorage.getItem("token") && btnRef.current)
      ) {
        btnRef.current.click();
      }
    }, 1000);

    const resizeHandler = (event) => {
      const resizeObserverError = /^ResizeObserver loop/;

      if (resizeObserverError.test(event.message)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener("error", resizeHandler);

    return () => {
      window.removeEventListener("error", resizeHandler);
      clearTimeout(timer);
    };
  }, []);

  return (
    <LanguageProvider>
      <HelmetProvider>
        <Provider store={store}>
          <PersistGate loading={<Loader />} persistor={persistor}>
            <GoogleOAuthProvider
              clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
            >
              <BrowserRouter>
                <ThemeProvider>
                  <ScrollToTop />
                  <StyledSnackbarProvider
                    hideIconVariant
                    autoHideDuration={3000}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  >
                    <Layout />
                  </StyledSnackbarProvider>
                </ThemeProvider>
              </BrowserRouter>
            </GoogleOAuthProvider>
          </PersistGate>
        </Provider>
        <button
          onClick={() => {
            requestPermission();
          }}
          ref={btnRef}
          style={{ position: "fixed", visibility: "hidden" }}
        >
          Enable Notification
        </button>
      </HelmetProvider>
    </LanguageProvider>
  );
};

export default App;
